// src/components/portal/DataRightsPanel.jsx
// SPRINT-DPDP-DATA-RIGHTS-001 · Phase D
// ADR-DPDP-DATA-RIGHTS-001 §7, §8 — client-facing entry points.
//
// ⚠️ INTEGRATION NOTE: this is a self-contained component, not a drop-in
// replacement for ClientPortalDashboard.jsx/.js. That file isn't in Project
// Knowledge (only ClientPortal.js, a differently-named file, is) — rather
// than guess at rewriting a file I haven't seen, this ships as its own
// component. Drop it into the real dashboard like:
//
//   import DataRightsPanel from '../components/portal/DataRightsPanel';
//   ...
//   <DataRightsPanel portalToken={token} clientData={clientData} />
//
// It needs exactly two things from the parent: the magic-link JWT string
// (same `token` already used for getClientData/verifyClientMagicToken —
// see ClientPortal.js's searchParams.get("token") for where that already
// lives) and the client's own data object (for prefilling the request form
// with their email/phone — optional, the form works without it).
//
// Both actions here are read-only or request-only from the client's
// perspective: "Export My Data" never modifies source data (ADR §7), and
// "Request Data Erasure" only creates a Submitted request — no destructive
// action happens client-side at all; everything past that goes through the
// reviewer queue (ErasureRequestQueuePage.jsx).

import React, { useState } from 'react';
import DpdpService from '../../services/DpdpService';
import { ShieldAlert, Download, X, CheckCircle, Loader2, FileText } from 'lucide-react';

const RequestErasureModal = ({ portalToken, clientData, onClose }) => {
  const [requesterEmail, setRequesterEmail] = useState(clientData?.email || '');
  const [requesterPhone, setRequesterPhone] = useState(clientData?.phone || '');
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!requesterEmail && !requesterPhone) {
      setError('Please provide at least your email or phone number so we can verify your identity.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await DpdpService.requestErasureFromPortal(portalToken, { requesterEmail, requesterPhone, remarks });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to submit your request. Please try again or contact the firm directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-indigo-700" />
            Request Data Erasure
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          {success ? (
            <div className="text-center py-4">
              <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-800">Request submitted</p>
              <p className="text-xs text-slate-500 mt-2">
                The firm will verify your identity before reviewing your request. This can take some time — data
                required for active case representation or statutory retention may need to be kept even after
                review. You'll be notified once it's processed.
              </p>
              <button onClick={onClose} className="mt-4 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800">
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4">
                Under the Digital Personal Data Protection Act, 2023, you may request erasure of your personal
                data. Data the firm is required to retain for active case representation, statutory limitation
                periods, or tax/audit purposes cannot be erased immediately — you'll receive a certificate
                explaining what was erased, anonymized, or retained and why.
              </p>

              <label className="text-xs font-semibold text-slate-700 mb-1 block">Email on file</label>
              <input
                type="email"
                value={requesterEmail}
                onChange={(e) => setRequesterEmail(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="you@example.com"
              />

              <label className="text-xs font-semibold text-slate-700 mb-1 block">Phone on file (optional if email given)</label>
              <input
                type="tel"
                value={requesterPhone}
                onChange={(e) => setRequesterPhone(e.target.value)}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="+91-XXXXXXXXXX"
              />

              <label className="text-xs font-semibold text-slate-700 mb-1 block">Anything else the firm should know? (optional)</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />

              {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 mt-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DataRightsPanel = ({ portalToken, clientData }) => {
  const [showErasureModal, setShowErasureModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportResult, setExportResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    setExportResult(null);
    try {
      const result = await DpdpService.exportMyDataFromPortal(portalToken);
      setExportResult(result);
      // Trigger the machine-readable JSON as an immediate download.
      const blob = new Blob([JSON.stringify(result.machineReadable, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || 'Failed to export your data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Your Data Rights</h3>
      <p className="text-xs text-slate-500 mb-4">Under the Digital Personal Data Protection Act, 2023.</p>

      <div className="space-y-2.5">
        <button
          onClick={() => setShowErasureModal(true)}
          className="w-full flex items-center gap-2.5 px-4 py-2.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
        >
          <ShieldAlert className="w-4 h-4 text-indigo-600" />
          Request Data Erasure
        </button>

        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full flex items-center gap-2.5 px-4 py-2.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Download className="w-4 h-4 text-slate-500" />}
          {exporting ? 'Preparing your data...' : 'Export My Data'}
        </button>
      </div>

      {exportError && <p className="text-xs text-red-600 mt-2">{exportError}</p>}

      {exportResult && (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-xs font-medium text-green-800 flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" /> Your machine-readable data has downloaded (my-data.json).
          </p>
          {exportResult.lawyerFriendlyZipUrl && (
            <a
              href={exportResult.lawyerFriendlyZipUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-green-700 underline hover:no-underline mt-1.5 inline-flex items-center gap-1"
            >
              <FileText className="w-3 h-3" /> Download your documents (ZIP, link expires in 24 hours)
            </a>
          )}
        </div>
      )}

      {showErasureModal && (
        <RequestErasureModal
          portalToken={portalToken}
          clientData={clientData}
          onClose={() => setShowErasureModal(false)}
        />
      )}
    </div>
  );
};

export default DataRightsPanel;
